import { CheckCircleRounded, ExpandMore } from "@mui/icons-material";
import { Accordion, AccordionDetails, AccordionSummary, Grid, TextField, useTheme } from "@mui/material";
import { SyntheticEvent, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Controller, useFormContext, useFormState, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { cbErrorSectionValidationState, isGlobalEditModeState, isMobilityVoucherModeState, provincesState, sapEnumState } from "../state/customerbaseState";
import { CBCountry, CBCreationOfProps, CBGoogleAutocompleteType, CBInvoicingCode, CBLabel, CBPartnerType, CBRoleKey, CBValuableLength, CBYesNoType, CustomerSearchFilters, IFormValues, IMapField, LEGAL_FORM_TO_FILTER_KEY, PA_ID_NUMBER_DEFAULT_VALUE, sapConfCountry, YES_NO_OPTIONS } from "../types";
import UpdateBtnsAccordion from "./UpdateBtnsAccordion";
import { extractGooglePlaceLocalityLongName, extractGooglePlaceTypeLongName, extractGooglePlaceTypeShortName, filterArray, getErrorKey } from "../utils";
import { getSapConfigurationByCountry, verifyTaxCodeAsync } from "../api/service/customerbaseApi";
import { GlobalContext } from "../../../util/state/GlobalContextProvider";
import { setSnackbar } from "../../../util/state/globalAction";
import { snackbarTypes } from "../../../util/state/globalVariables";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import CBDialogSearchCustomer from "./modal/CBDialogSearchCustomer";
import { CustomerBaseDto } from "../types";
import CBFormTextFieldController from "./formController/CBFormTextFieldController";
import FormController from "./formController/FormController";
import CBCountryController from "./formController/CBCountryController";
import CBProvinceController from "./formController/CBProvinceController";
import CBGoogleAutocompleteController from "./formController/CBGoogleAutocompleteController";
import CBGoogleAutocompleteSearchBar from "./CBGoogleAutocompleteSearchBar";
import CBIntegrationSearch from "./CBIntegrationSearch";
import { Data } from "@react-google-maps/api";

interface CustomerBasePersonalDetailsProps extends CBCreationOfProps {
    handleCustomerSelected: (selectedCustomer: CustomerBaseDto) => void,
    handleCheckCustomerExistenceOnBlur: (labelCheck: string) => void
}

const CustomerBasePersonalDetails = ({ creationOf, handleCustomerSelected, handleCheckCustomerExistenceOnBlur }: CustomerBasePersonalDetailsProps) => {

    const { dispatchGlobalData } = useContext(GlobalContext);
    const { t } = useTranslation(['customerbase']);
    const theme = useTheme();
    const { id } = useParams();
    const isCreationMode = !!creationOf;
    const formContext = useFormContext<IFormValues>();
    const [isExpanded, setIsExpanded] = useState(isCreationMode);
    const isGlobalEditMode = useRecoilValue(isGlobalEditModeState);
    const editing = isGlobalEditMode;

    const sapEnum = useRecoilValue(sapEnumState);
    const isPerson = formContext.getValues("cbDetails.partnerType") == CBPartnerType.PERSON;

    const [openSearchDialog, setOpenSearchDialog] = useState(false);
    const [customerFilters, setCustomerFilters] = useState<CustomerSearchFilters | null>(null);

    const provinceOptions = useRecoilValue(provincesState);
    const birthCountry = useWatch<IFormValues, 'cbPersonalInfo.birthCountry'>({ name: "cbPersonalInfo.birthCountry" });
    const birthCity = useWatch<IFormValues, 'cbPersonalInfo.birthCity'>({ name: "cbPersonalInfo.birthCity" });
    const residenceCountry = useWatch<IFormValues, 'cbPersonalInfo.residenceCountry'>({ name: "cbPersonalInfo.residenceCountry" });
    const publicAuthorityWatch = useWatch<IFormValues, 'cbPersonalInfo.publicAuthority'>({ name: "cbPersonalInfo.publicAuthority" });
    const residenceProvinceWatch = useWatch<IFormValues, 'cbPersonalInfo.residenceProvince'>({ name: "cbPersonalInfo.residenceProvince" });
    const roleWatch = useWatch<IFormValues, 'cbDetails.role'>({ name: "cbDetails.role" });
    const cfValidatedWatch = useWatch<IFormValues, 'cbPersonalInfo.cfValidated'>({ name: "cbPersonalInfo.cfValidated" });
    const vatNumberCountryWatch = useWatch<IFormValues, 'cbPersonalInfo.vatNumberCountry'>({ name: "cbPersonalInfo.vatNumberCountry" });

    const [legalFormOptions, setLegalFormOptions] = useState<IMapField[]>([]);

    const setErrorSection = useSetRecoilState(cbErrorSectionValidationState);
    const isMobilityVoucherMode = useRecoilValue(isMobilityVoucherModeState);

    const [sapConfByCountry, setSapConfByCountry] = useState<sapConfCountry>({
        legalForms: [],
        paymentTerms: []
    })

    //Usato solo per legalForm
    useEffect(() => {
        if (!vatNumberCountryWatch){
            setLegalFormOptions([])
            return;
        };

        getSapConfigurationByCountry(vatNumberCountryWatch)
            .then(result => {
                const data: sapConfCountry = result.data;
                setSapConfByCountry(data);
            })
            .catch(e => {
                console.error('Errore nella chiamata:', e);
                setSapConfByCountry({legalForms: [], paymentTerms: []});
            })
    }, [vatNumberCountryWatch])


    const handleTaxCodeOnBlur = useCallback(async () => {
        //CONTROLLI NECESSARI SOLO PER UNA PERSON
        if (!isPerson) return;

        const cbPersonalInfo = formContext.getValues("cbPersonalInfo");
        console.log("Verify Params: ", cbPersonalInfo);

        let canCheckCustomerExistence: boolean = true;

        if (cbPersonalInfo.taxCode?.length == CBValuableLength.TAX_CODE && !!cbPersonalInfo.firstName && !!cbPersonalInfo.lastName && !!cbPersonalInfo.gender
            && !!cbPersonalInfo.birthDate && /*!!cbPersonalInfo.birthProvince &&*/ !!cbPersonalInfo.birthCity
            && (cbPersonalInfo.gender !== "--" /*&& cbPersonalInfo.birthProvince !== "--"*/ && cbPersonalInfo.birthCity !== "--")) {

            /**18/05/23:
             * Per un utente con birthCountry != IT ma in possesso di CF si effettua la verifyTaxCode
             * passando la birthcCountry nel campo birthcity della request
             * In generale, la birthProvince non è più obbligatoria
             * Decade anche la necessità di validare la residenceCountry
             */
            const isValid = await formContext.trigger("cbPersonalInfo.taxCode");
            console.log("Trigger: ", isValid);

            if (isValid) {
                try {
                    const resp = await verifyTaxCodeAsync({
                        firstName: cbPersonalInfo.firstName,
                        lastName: cbPersonalInfo.lastName,
                        gender: cbPersonalInfo.gender,
                        birthDate: cbPersonalInfo.birthDate,
                        birthProvince: !!cbPersonalInfo.birthProvince && cbPersonalInfo.birthProvince !== "--" ? cbPersonalInfo.birthProvince : undefined,
                        birthCity: !!cbPersonalInfo.birthCountry && cbPersonalInfo.birthCountry !== "--" && cbPersonalInfo.birthCountry !== CBCountry.ITALY ? cbPersonalInfo.birthCountry : cbPersonalInfo.birthCity,
                        taxCode: cbPersonalInfo.taxCode
                    });

                    console.log("AWAIT RESP: ", resp);

                } catch (error: any) {
                    console.log("AWAIT ERR: ", error);

                    canCheckCustomerExistence = false;

                    dispatchGlobalData({
                        type: setSnackbar,
                        payload: {
                            snackbar: {
                                message: t(`message.error.${getErrorKey(error)}`),
                                type: snackbarTypes.error,
                                show: true,
                                autoClose: true
                            }
                        }
                    })
                }

            } else canCheckCustomerExistence = false;
        }

        if (canCheckCustomerExistence) handleCheckCustomerExistenceOnBlur(CBLabel.TAX_CODE_LABEL_CHECK);

    }, [id, isPerson, formContext, handleCheckCustomerExistenceOnBlur]);

    const handleSearchClick = useCallback((e: SyntheticEvent) => {
        e.stopPropagation();

        const cbProfile = formContext.getValues("cbPersonalInfo");

        if ((isPerson && !cbProfile.firstName && !cbProfile.lastName && !cbProfile.taxCode
            && !cbProfile.drvLicenceNumber) ||
            (!isPerson && !cbProfile.businessName && !cbProfile.vatNumberCountry && !cbProfile.vatNumber)) {
            if (isPerson) {
                formContext.setError("cbPersonalInfo.firstName", {
                    message: t("message.validation.atLeastOneRequired"),
                    type: "custom"
                });
                formContext.setError("cbPersonalInfo.lastName", {
                    message: t("message.validation.atLeastOneRequired"),
                    type: "custom"
                });
                formContext.setError("cbPersonalInfo.taxCode", {
                    message: t("message.validation.atLeastOneRequired"),
                    type: "custom"
                });
                formContext.setError("cbPersonalInfo.drvLicenceNumber", {
                    message: t("message.validation.atLeastOneRequired"),
                    type: "custom"
                });
            } else {

                formContext.setError("cbPersonalInfo.businessName", {
                    message: t("message.validation.atLeastOneRequired"),
                    type: "custom"
                });
                formContext.setError("cbPersonalInfo.vatNumberCountry", {
                    message: t("message.validation.atLeastOneRequired"),
                    type: "custom"
                });
                formContext.setError("cbPersonalInfo.vatNumber", {
                    message: t("message.validation.atLeastOneRequired"),
                    type: "custom"
                });

            }
        } else {
            if (!isPerson) {
                if (!!cbProfile.vatNumberCountry && !cbProfile.vatNumber) {
                    formContext.setError("cbPersonalInfo.vatNumber", {
                        message: t("message.validation.requiredField"),
                        type: "custom"
                    });
                }
                else if (!cbProfile.vatNumberCountry && !!cbProfile.vatNumber) {
                    formContext.setError("cbPersonalInfo.vatNumberCountry", {
                        message: t("message.validation.requiredField"),
                        type: "custom"
                    });
                } else {
                    setCustomerFilters(
                        {
                            businessName: cbProfile.businessName || null,
                            vatNumber: !!cbProfile.vatNumberCountry && !!cbProfile.vatNumber ? (cbProfile.vatNumberCountry + cbProfile.vatNumber) : (cbProfile.vatNumberCountry || cbProfile.vatNumber || null),
                            partnerType: CBPartnerType.ORGANIZATION
                        }
                    );
                    setOpenSearchDialog(true);

                    formContext.clearErrors(
                        [
                            "cbPersonalInfo.businessName",
                            "cbPersonalInfo.vatNumberCountry",
                            "cbPersonalInfo.vatNumber"
                        ]
                    );

                }
            } else {
                setCustomerFilters(
                    {
                        firstName: cbProfile.firstName || null,
                        lastName: cbProfile.lastName || null,
                        taxCode: cbProfile.taxCode || null,
                        drvLicenceNumber: cbProfile.drvLicenceNumber || null,
                        partnerType: CBPartnerType.PERSON
                    }
                );
                setOpenSearchDialog(true);

                formContext.clearErrors(
                    [
                        "cbPersonalInfo.firstName",
                        "cbPersonalInfo.lastName",
                        "cbPersonalInfo.taxCode",
                        "cbPersonalInfo.drvLicenceNumber"
                    ]
                );

            }
        }

    }, [formContext, isPerson]);

    useEffect(() => {
        if (!birthCity) {
            formContext.resetField("cbPersonalInfo.birthProvince");
            formContext.resetField("cbPersonalInfo.birthCountry");
        }

    }, [birthCity]);

    // useEffect(() => {
    //     if(isPerson) return;
    //     if((isCreationMode || editing) && sapEnum.legalForms?.length > 0)
    //      setLegalFormOptions(filterArray(sapEnum.legalForms, LEGAL_FORM_TO_FILTER_KEY));
    //     else
    //      setLegalFormOptions(sapEnum.legalForms || [])

    // }, [sapEnum, isCreationMode, editing, isPerson])
    useEffect(() => {
        if (isPerson) return;
        if ((isCreationMode || editing) && sapConfByCountry?.legalForms.length > 0){
            setLegalFormOptions(filterArray(sapConfByCountry.legalForms, LEGAL_FORM_TO_FILTER_KEY));
        }
        else
            setLegalFormOptions(sapConfByCountry.legalForms || [])

    }, [sapEnum, isCreationMode, editing, isPerson,sapConfByCountry])

    return (
        <>
            <CBDialogSearchCustomer
                key='cb-integration-search-dialog'
                isPerson={isPerson}
                open={openSearchDialog}
                onClose={() => {
                    setOpenSearchDialog(false);
                    setCustomerFilters(null);
                }}
                onCustomerSelected={handleCustomerSelected}
                customerFilters={customerFilters}
            />

            <Accordion expanded={isExpanded} onChange={(_e, expanded) => {
                setIsExpanded(expanded);

            }}>
                <AccordionSummary expandIcon={<ExpandMore />}>
                    {isCreationMode ?
                        <CBIntegrationSearch
                            isPerson={isPerson}
                            handleSearchClick={handleSearchClick}
                        />
                        :
                        <UpdateBtnsAccordion
                            section="cbPersonalOrganizationInfo"
                            label={isPerson ? t("tabs.accordionsLabels.cbPersonalDetails") : t("tabs.accordionsLabels.cbOrganizationInfo")}
                            editing={editing}
                        />
                    }
                </AccordionSummary>
                <AccordionDetails>
                    <Grid container columnSpacing={3} rowSpacing={1}>
                        {isPerson &&
                            <>
                                <Grid item md={6} xs={12}>
                                    <CBFormTextFieldController
                                        formName={"cbPersonalInfo.firstName"}
                                        label={"firstName"}
                                        readOnly={isCreationMode ? false : !editing}
                                        required={isPerson}
                                        isDisabled={(isCreationMode || editing) && !!cfValidatedWatch}
                                    />
                                </Grid>
                                <Grid item md={6} xs={12}>
                                    <CBFormTextFieldController
                                        formName={"cbPersonalInfo.lastName"}
                                        label={"lastName"}
                                        readOnly={isCreationMode ? false : !editing}
                                        required={isPerson}
                                        isDisabled={(isCreationMode || editing) && !!cfValidatedWatch}
                                    />
                                </Grid>
                                <Grid item md={6} xs={12}>
                                    <Controller
                                        control={formContext.control}
                                        name={"cbPersonalInfo.birthDate"}
                                        render={({ field, fieldState }) =>
                                            <DatePicker
                                                readOnly={isCreationMode ? false : !editing}
                                                label={t("birthDate") + (isPerson ? ' *' : '')}
                                                value={field.value == "--" ? null : (field.value || null)}
                                                disabled={(isCreationMode || editing) && !!cfValidatedWatch}
                                                onChange={(value) => {
                                                    field.onChange(dayjs(value).format("YYYY-MM-DD"));
                                                }}
                                                inputFormat={"DD-MM-YYYY"}
                                                InputProps={{
                                                    autoComplete: 'none'
                                                }}
                                                renderInput={(params: any) => (
                                                    <TextField
                                                        fullWidth
                                                        id="birthDate"
                                                        label={t("birthDate")}
                                                        {...params}
                                                        error={Boolean(fieldState.error)}
                                                        helperText={fieldState.error?.message}
                                                    />
                                                )}
                                            />
                                        }
                                    />
                                </Grid>
                                <Grid item md={6} xs={12}>
                                    <FormController
                                        formName="cbPersonalInfo.gender"
                                        label="gender"
                                        isMandatory
                                        options={sapEnum.genders || []}
                                        showSelect={isCreationMode || editing}
                                        disabled={(isCreationMode || editing) && !!cfValidatedWatch}
                                    />
                                </Grid>
                                <Grid item md={6} xs={12}>
                                    {
                                        <CBGoogleAutocompleteController
                                            formName={"cbPersonalInfo.birthCity"}
                                            idField="birthCity"
                                            label={"birthCity"}
                                            showSelect={isCreationMode || editing}
                                            isDisabled={(isCreationMode || editing) && !!cfValidatedWatch}
                                            optionsTypes={[CBGoogleAutocompleteType.CITIES]}
                                            handleOnPlaceSelected={(place: any) => {

                                                const country = extractGooglePlaceTypeShortName(place, CBGoogleAutocompleteType.COUNTRY);

                                                if (!!country && country == CBCountry.ITALY) {
                                                    const province = extractGooglePlaceTypeShortName(place, CBGoogleAutocompleteType.PROVINCE);
                                                    formContext.setValue("cbPersonalInfo.birthProvince", province || '', { shouldDirty: true });
                                                }

                                                formContext.setValue("cbPersonalInfo.birthCountry", country || '', { shouldDirty: true });

                                                //return extractGooglePlaceTypeLongName(place, CBGoogleAutocompleteType.LOCALITY);   
                                                return extractGooglePlaceLocalityLongName(place);
                                            }}
                                        />
                                    }
                                </Grid>
                                <Grid item md={6} xs={12}>
                                    {
                                        <CBProvinceController
                                            key={"birth-province-controller"}
                                            formName={"cbPersonalInfo.birthProvince"}
                                            label={"birthProvince"}
                                            provinceOptions={provinceOptions.filter(p => p.country == birthCountry).map(p => ({
                                                key: p.codeProvince,
                                                value: p.description
                                            }))}
                                            showSelect={isCreationMode || editing}
                                            isDisabled={(isCreationMode || editing) && !!cfValidatedWatch}
                                        />
                                    }
                                </Grid>
                                <Grid item md={6} xs={12}>
                                    {
                                        <CBCountryController
                                            formName={"cbPersonalInfo.birthCountry"}
                                            label={"birthCountry"}
                                            readOnly={isCreationMode ? false : !editing}
                                            showSelect={isCreationMode || editing}
                                            isDisabled={(isCreationMode || editing) && !!cfValidatedWatch}
                                            //isDisabled
                                            handleBlankedValueOnChange={() => {
                                                formContext.setValue("cbPersonalInfo.birthProvince", '');
                                            }}
                                        />
                                    }
                                </Grid>
                                <Grid item md={6} xs={12} />
                                <Grid item md={6} xs={12}>
                                    <Controller
                                        control={formContext.control}
                                        name={"cbPersonalInfo.taxCode"}
                                        render={({ field, fieldState }) => (
                                            <TextField
                                                {...field}
                                                fullWidth
                                                InputProps={{
                                                    readOnly: isCreationMode ? false : !editing,
                                                    autoComplete: 'none',
                                                    endAdornment: !!cfValidatedWatch ? <CheckCircleRounded sx={{ color: theme.palette.success.main }} /> : null
                                                }}
                                                inputProps={{ maxLength: 16, style: { textTransform: "uppercase" } }}
                                                disabled={(isCreationMode || editing) && !!cfValidatedWatch}
                                                id="taxCode"
                                                label={t("taxCode")}
                                                type="text"
                                                value={field.value}
                                                onBlur={handleTaxCodeOnBlur}
                                                error={Boolean(fieldState.error)}
                                                helperText={fieldState.error?.message}

                                            />
                                        )}
                                    />
                                </Grid>
                                <Grid item md={6} xs={12}>
                                    <CBFormTextFieldController
                                        formName={"cbPersonalInfo.drvLicenceNumber"}
                                        label={"drvLicenceNumber"}
                                        readOnly={isCreationMode ? false : !editing}
                                        onBlur={() => handleCheckCustomerExistenceOnBlur(CBLabel.DRIVING_LICENCE_LABEL_CHECK)}
                                    />
                                </Grid>
                            </>
                        }
                        {!isPerson &&
                            <>
                                <Grid item md={6} xs={12}>
                                    <FormController
                                        formName="cbPersonalInfo.legalForm"
                                        label="legalForm"
                                        translationLabel="sapEnum.legalForms"
                                        options={legalFormOptions}
                                        showSelect={isCreationMode || editing}
                                        disabled={vatNumberCountryWatch === null}
                                        isMandatory
                                        handleAdditionalBehaviourOnChange={(_value) => {
                                            if (isCreationMode && !isPerson) {
                                                formContext.trigger(["cbPersonalInfo.vatNumberCountry", "cbPersonalInfo.vatNumber"]);

                                                if (!!formContext.getValues("cbPersonalInfo.taxCode"))
                                                    formContext.trigger("cbPersonalInfo.taxCode");
                                            }

                                        }}
                                    />
                                </Grid>
                                <Grid item md={6} xs={12}>
                                    <CBFormTextFieldController
                                        formName={"cbPersonalInfo.businessName"}
                                        label={"businessName"}
                                        readOnly={isCreationMode ? false : !editing}
                                        required={!isPerson}
                                    />
                                </Grid>
                                <Grid item md={6} xs={12}>
                                    {/*<CBFormTextFieldController 
                                        formName={"cbPersonalInfo.vatNumberCountry"}
                                        label={"vatNumberCountry"}
                                        readOnly={isCreationMode ? false : !editing}
                                    />*/}
                                    {
                                        <CBCountryController
                                            formName={"cbPersonalInfo.vatNumberCountry"}
                                            label={"vatNumberCountry"}
                                            idField={"vat-number-country"}
                                            readOnly={isCreationMode ? false : !editing}
                                            showSelect={isCreationMode || editing}
                                            handleAdditionalBehaviourOnBlur={() => {
                                                formContext.setValue("cbPersonalInfo.siren", '');
                                                formContext.setValue("cbPersonalInfo.siret", '');
                                                handleCheckCustomerExistenceOnBlur(CBLabel.VAT_NUMBER_LABEL_CHECK)
                                            }}
                                        //isMandatory
                                        />
                                    }
                                </Grid>
                                <Grid item md={6} xs={12}>
                                    <CBFormTextFieldController
                                        formName={"cbPersonalInfo.vatNumber"}
                                        label={"vatNumber"}
                                        readOnly={isCreationMode ? false : !editing}
                                        //isMandatory
                                        onBlur={() => {
                                            //handleVatNumberOnBlur();
                                            handleCheckCustomerExistenceOnBlur(CBLabel.VAT_NUMBER_LABEL_CHECK);
                                        }}
                                    />
                                </Grid>
                                {(!!vatNumberCountryWatch && vatNumberCountryWatch == CBCountry.FRANCE) &&
                                    <>
                                        <Grid item md={6} xs={12}>
                                            <CBFormTextFieldController
                                                formName={"cbPersonalInfo.siren"}
                                                label={"siren"}
                                                readOnly={isCreationMode ? false : !editing}
                                                required
                                                type="number"
                                            />
                                        </Grid>
                                        <Grid item md={6} xs={12}>
                                            <CBFormTextFieldController
                                                formName={"cbPersonalInfo.siret"}
                                                label={"siret"}
                                                readOnly={isCreationMode ? false : !editing}
                                                type="number"
                                            />
                                        </Grid>
                                    </>
                                }
                                <Grid item md={6} xs={12}>
                                    <Controller
                                        control={formContext.control}
                                        name={"cbPersonalInfo.taxCode"}
                                        render={({ field, fieldState }) => (
                                            <TextField
                                                {...field}
                                                fullWidth
                                                InputProps={{
                                                    readOnly: isCreationMode ? false : !editing,
                                                    autoComplete: 'none'
                                                }}
                                                inputProps={{ maxLength: 16, style: { textTransform: "uppercase" } }}
                                                id="companyFiscalCode"
                                                label={t("companyFiscalCode")}
                                                type="text"
                                                //required={residenceCountry == CBCountry.ITALY}
                                                value={field.value}
                                                onBlur={() => handleCheckCustomerExistenceOnBlur(CBLabel.TAX_CODE_LABEL_CHECK)}
                                                /*onChange={(e) => {
                                                    field.onChange(e.currentTarget.value);
                                                    formContext.setValue("cbInvoicingAddress.invoicingCompanyFiscalCode", e.currentTarget.value || '', {shouldDirty: true});
                                                }}*/
                                                error={Boolean(fieldState.error)}
                                                helperText={fieldState.error?.message}
                                            />

                                        )}
                                    />
                                </Grid>
                                {/*10/05/23: SI NASCONDE*/
                                /*<Grid item md={6} xs={12}/>
                                <Grid item md={6} xs={12}>
                                    <FormController 
                                        formName="cbPersonalInfo.dealerType"
                                        label="dealerType"
                                        options={sapEnum.dealerTypes || []}
                                        showSelect={isCreationMode || editing}
                                    />
                                </Grid>*/}
                                <Grid item md={6} xs={12}>
                                    <FormController
                                        formName="cbPersonalInfo.customerType"
                                        label="customerType"
                                        options={sapEnum.customerTypes || []}
                                        showSelect={isCreationMode || editing}
                                    />
                                </Grid>
                                <Grid item md={6} xs={12}>
                                    <FormController
                                        formName="cbPersonalInfo.publicAuthority"
                                        label="publicAuthority"
                                        translationLabel="yesNoOptions"
                                        options={YES_NO_OPTIONS || []}
                                        showSelect={isCreationMode || editing}
                                        isMandatory
                                        sendValue
                                        handleAdditionalBehaviourOnChange={(value) => {
                                            if (!!value) {
                                                //console.log("role: ", roleWatch);

                                                if (value.value == CBYesNoType.NO) {

                                                    formContext.setValue("cbPersonalInfo.paIdNumber", '');
                                                    formContext.trigger("cbPersonalInfo").then((isValid) => {
                                                        setErrorSection((prev) => {
                                                            return { ...prev, ["cbPersonalOrganizationInfo"]: !isValid }
                                                        });
                                                    });

                                                    if (isCreationMode && roleWatch.includes(CBRoleKey.SOLD_TO_PARTY)) {
                                                        if (!formContext.getValues("cbInvoicingAddress.invoicingRecipientCode"))
                                                            formContext.setValue("cbInvoicingAddress.invoicingRecipientCode", CBInvoicingCode.DEFAULT);

                                                        formContext.trigger("cbInvoicingAddress.invoicingRecipientCode");
                                                    }

                                                }
                                                else {
                                                    if (isCreationMode)
                                                        formContext.resetField("cbInvoicingAddress.invoicingRecipientCode");

                                                    formContext.setValue("cbPersonalInfo.paIdNumber", PA_ID_NUMBER_DEFAULT_VALUE);
                                                }
                                            }
                                        }}

                                    />
                                </Grid>
                                {publicAuthorityWatch == CBYesNoType.YES &&
                                    <Grid item md={6} xs={12}>
                                        <CBFormTextFieldController
                                            formName="cbPersonalInfo.paIdNumber"
                                            label="paIdNumber"
                                            readOnly={isCreationMode ? false : !editing}
                                            required
                                            inputProps={{ maxLength: 50 }}
                                        />
                                    </Grid>
                                }
                            </>
                        }
                        <Grid item container columnSpacing={3} rowSpacing={1} marginTop={5}>
                            {(isCreationMode || editing) &&
                                <Grid item md={12} xs={12}>
                                    <CBGoogleAutocompleteSearchBar
                                        idField="cb-residence-google-autocomplete-search-bar"
                                        onPlaceSelected={(place) => {
                                            const country = extractGooglePlaceTypeShortName(place, CBGoogleAutocompleteType.COUNTRY);

                                            if (!!country && country == CBCountry.ITALY) {
                                                const province = extractGooglePlaceTypeShortName(place, CBGoogleAutocompleteType.PROVINCE);
                                                formContext.setValue("cbPersonalInfo.residenceProvince", province || '', { shouldDirty: true, shouldValidate: true });
                                            }

                                            const city = extractGooglePlaceLocalityLongName(place);
                                            const postalCode = extractGooglePlaceTypeLongName(place, CBGoogleAutocompleteType.POSTAL_CODE);
                                            const streetName = extractGooglePlaceTypeLongName(place, CBGoogleAutocompleteType.ROUTE);
                                            const streetNumber = extractGooglePlaceTypeLongName(place, CBGoogleAutocompleteType.STREET_NUMBER);

                                            formContext.setValue("cbPersonalInfo.residenceCountry", country || '', { shouldDirty: true, shouldValidate: true });
                                            formContext.setValue("cbPersonalInfo.residenceCity", city || '', { shouldDirty: true, shouldValidate: true });
                                            formContext.setValue("cbPersonalInfo.residenceZipCode", postalCode || '', { shouldDirty: true, shouldValidate: true });
                                            formContext.setValue("cbPersonalInfo.residenceStreet", streetName || '', { shouldDirty: true, shouldValidate: true });
                                            formContext.setValue("cbPersonalInfo.residenceHouseNumber", streetNumber || '', { shouldDirty: true, shouldValidate: true });

                                            if (!!country) {
                                                handleCheckCustomerExistenceOnBlur(CBLabel.DRIVING_LICENCE_LABEL_CHECK)
                                            }

                                        }}
                                        onChange={(event) => {
                                            if (!event.currentTarget.value) {

                                                formContext.resetField("cbPersonalInfo.residenceCountry");
                                                formContext.resetField("cbPersonalInfo.residenceProvince");
                                                formContext.resetField("cbPersonalInfo.residenceCity");
                                                formContext.resetField("cbPersonalInfo.residenceZipCode");
                                                formContext.resetField("cbPersonalInfo.residenceStreet");
                                                formContext.resetField("cbPersonalInfo.residenceHouseNumber")

                                                formContext.trigger([
                                                    "cbPersonalInfo.residenceCountry",
                                                    "cbPersonalInfo.residenceProvince",
                                                    "cbPersonalInfo.residenceCity",
                                                    "cbPersonalInfo.residenceZipCode",
                                                    "cbPersonalInfo.residenceStreet",
                                                    "cbPersonalInfo.residenceHouseNumber"
                                                ]);
                                            }

                                        }}
                                    />
                                </Grid>
                            }
                            <Grid item md={6} xs={12}>
                                {
                                    <CBCountryController
                                        formName={"cbPersonalInfo.residenceCountry"}
                                        label={isPerson ? "residenceCountry" : "legalHeadOfficeCountry"}
                                        idField={isPerson ? "residence-country" : "legal.head-office-country"}
                                        readOnly={isCreationMode ? false : !editing}
                                        showSelect={isCreationMode || editing}
                                        isMandatory
                                        handleBlankedValueOnChange={() => {
                                            formContext.setValue("cbPersonalInfo.residenceProvince", '');
                                        }}
                                        handleAdditionalBehaviourOnBlur={() => {
                                            formContext.trigger("cbPersonalInfo.residenceProvince");
                                            handleCheckCustomerExistenceOnBlur(CBLabel.DRIVING_LICENCE_LABEL_CHECK)
                                        }}
                                    />
                                }
                            </Grid>
                            <Grid item md={6} xs={12}>
                                {
                                    <CBProvinceController
                                        key={"residence-province-controller"}
                                        formName={"cbPersonalInfo.residenceProvince"}
                                        idField={isPerson ? "residence-province" : "legal-head-office-province"}
                                        label={isPerson ? "residenceProvince" : "legalHeadOfficeProvince"}
                                        provinceOptions={provinceOptions.filter(p => p.country == residenceCountry).map(p => ({
                                            key: p.codeProvince,
                                            value: p.description
                                        }))}
                                        showSelect={isCreationMode || editing}
                                        isMandatory={isMobilityVoucherMode}
                                    />
                                }
                            </Grid>
                            <Grid item md={6} xs={12}>
                                {
                                    <CBFormTextFieldController
                                        formName={"cbPersonalInfo.residenceCity"}
                                        idField={isPerson ? "residence-city" : "legal-head-office-city"}
                                        label={isPerson ? "residenceCity" : "legalHeadOfficeCity"}
                                        readOnly={isCreationMode ? false : !editing}
                                        required={isMobilityVoucherMode}
                                    />
                                }
                            </Grid>
                            <Grid item md={6} xs={12}>
                                <CBFormTextFieldController
                                    formName={"cbPersonalInfo.residenceZipCode"}
                                    idField={isPerson ? "residence-zipcode" : "legal-head-office-zipcode"}
                                    label={isPerson ? "residencePostalCode" : "legalHeadOfficePostalCode"}
                                    readOnly={isCreationMode ? false : !editing}
                                    required={isMobilityVoucherMode}
                                />
                            </Grid>
                            <Grid item md={6} xs={12}>
                                {<CBFormTextFieldController
                                    formName={"cbPersonalInfo.residenceStreet"}
                                    idField={isPerson ? "residence-street" : "legal-head-office-street"}
                                    label={isPerson ? "residenceStreet" : "legalHeadOfficeStreet"}
                                    readOnly={isCreationMode ? false : !editing}
                                    required={isMobilityVoucherMode}
                                />}
                            </Grid>
                            <Grid item md={6} xs={12}>
                                <CBFormTextFieldController
                                    formName={"cbPersonalInfo.residenceHouseNumber"}
                                    idField={isPerson ? "residence-houseNumber" : "legal-head-office-houseNumber"}
                                    label={isPerson ? "residenceHouseNumber" : "legalHeadOfficeHouseNumber"}
                                    readOnly={isCreationMode ? false : !editing}
                                    required={isMobilityVoucherMode}
                                />
                            </Grid>
                        </Grid>
                        {/*20/06/2023: SI È DECISO DI NASCONDERLI*/
                        /*isPerson && <Grid item container columnSpacing={3} rowSpacing={1} marginTop={5}>
                            <Grid item md={6} xs={12}>
                                <CBFormTextFieldController 
                                    formName={"cbPersonalInfo.vatNumberCountry"}
                                    label={"vatNumberCountry"}
                                    readOnly={isCreationMode ? false : !editing}
                                />
                            </Grid>
                            <Grid item md={6} xs={12}>
                                <CBFormTextFieldController 
                                    formName={"cbPersonalInfo.vatNumber"}
                                    label={"vatNumber"}
                                    readOnly={isCreationMode ? false : !editing}
                                    isMandatory={!isPerson}
                                />
                            </Grid>
                            <Grid item md={6} xs={12}>
                                <CBFormTextFieldController 
                                    formName={"cbPersonalInfo.businessName"}
                                    label={"businessName"}
                                    readOnly={isCreationMode ? false : !editing}
                                    isMandatory={!isPerson}
                                />
                            </Grid>
                        </Grid>*/}
                    </Grid>
                </AccordionDetails>
            </Accordion>
        </>
    );

}

export default CustomerBasePersonalDetails;